// src/utils/firebase/publicProfile.js
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc
} from 'firebase/firestore';
import { db } from './firebase.utils';
import { serializeFirestoreData } from './serialization';

/**
 * Fields mirrored onto publicProfiles/{uid} for other users to read.
 * Email, tokens, and role stay on the private users/{uid} document.
 */
export const PUBLIC_PROFILE_FIELDS = [
  'username',
  'collectionCardCount',
  'collectionLastUpdated'
];

/**
 * Subset the client is allowed to write. `username` is owned by the
 * setUsername Cloud Function so a user cannot mirror someone else's handle;
 * the security rules reject client writes that touch it.
 */
export const CLIENT_WRITABLE_PUBLIC_PROFILE_FIELDS = [
  'collectionCardCount',
  'collectionLastUpdated'
];

/**
 * Builds a reference to a user's public profile document
 * @param {string} userID - User ID
 * @returns {import('firebase/firestore').DocumentReference}
 */
export const publicProfileDocRef = (userID) => doc(db, `publicProfiles/${userID}`);

const pickClientWritableFields = (data) =>
  CLIENT_WRITABLE_PUBLIC_PROFILE_FIELDS.reduce((fields, key) => {
    if (data?.[key] !== undefined) {
      fields[key] = data[key];
    }
    return fields;
  }, {});

/**
 * Fetches a user's public profile. Safe to call for any user ID.
 * @param {string} userID - User ID
 * @returns {Promise<Object|null>} Public profile data or null if not found
 */
export const fetchPublicProfile = async (userID) => {
  try {
    const snap = await getDoc(publicProfileDocRef(userID));
    return snap.exists() ? serializeFirestoreData(snap.data()) : null;
  } catch (error) {
    console.error('Error fetching public profile:', error);
    return null;
  }
};

/**
 * Mirrors the caller's own public fields onto publicProfiles/{uid}
 * @param {string} userID - User ID
 * @param {Object} data - Source data to pull public fields from
 * @returns {Promise<void>}
 */
export const syncPublicProfile = async (userID, data) => {
  const publicData = pickClientWritableFields(data);

  if (Object.keys(publicData).length === 0) {
    return;
  }

  try {
    await setDoc(publicProfileDocRef(userID), publicData, { merge: true });
  } catch (error) {
    console.error('Error syncing public profile:', error);
  }
};

/**
 * Queues the public profile mirror onto an existing batch so it commits
 * atomically with the user document update that triggered it.
 * @param {import('firebase/firestore').WriteBatch} batch - Batch to append to
 * @param {string} userID - User ID
 * @param {Object} data - Source data to pull public fields from
 */
export const syncPublicProfileInBatch = (batch, userID, data) => {
  const publicData = pickClientWritableFields(data);

  if (Object.keys(publicData).length === 0) {
    return;
  }

  batch.set(publicProfileDocRef(userID), publicData, { merge: true });
};

/**
 * Deletes a user's public profile
 * @param {string} userID - User ID
 * @returns {Promise<void>}
 */
export const deletePublicProfile = async (userID) => {
  await deleteDoc(publicProfileDocRef(userID));
};
